import type { TemplateSendAggregateRow } from "../types";

export const TEMPLATE_SEND_PROMINENT_SHARE_THRESHOLD = 10;

export interface TemplateSendSummary {
  totalSends: number;
  templateCount: number;
  topTemplateLabel: string | null;
  /** 0–100, uma casa decimal. */
  topTemplateSharePercent: number;
}

export interface PartitionedTemplateRows {
  prominent: TemplateSendAggregateRow[];
  longTail: TemplateSendAggregateRow[];
}

function roundSharePercent(part: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((part / total) * 1000) / 10;
}

/** Headline e totais para o foco em modelos (storytelling). */
export function buildTemplateSendSummary(
  rows: TemplateSendAggregateRow[],
): TemplateSendSummary {
  const totalSends = rows.reduce((sum, row) => sum + row.sendCount, 0);
  const templateCount = rows.length;
  const top = rows[0] ?? null;

  return {
    totalSends,
    templateCount,
    topTemplateLabel: top?.templateLabel ?? null,
    topTemplateSharePercent: roundSharePercent(top?.sendCount ?? 0, totalSends),
  };
}

export interface PartitionedBySendShare<T> {
  prominent: T[];
  longTail: T[];
}

/** Separa linhas de ranking por participação (≥ threshold vs cauda longa). */
export function partitionRowsByShare<T extends { sendCount: number }>(
  rows: T[],
  totalSends: number,
  thresholdPercent = TEMPLATE_SEND_PROMINENT_SHARE_THRESHOLD,
): PartitionedBySendShare<T> {
  const prominent: T[] = [];
  const longTail: T[] = [];

  for (const row of rows) {
    const share = templateSendSharePercent(row.sendCount, totalSends);
    if (share >= thresholdPercent) {
      prominent.push(row);
    } else {
      longTail.push(row);
    }
  }

  return { prominent, longTail };
}

/** Separa modelos relevantes (≥ threshold) da cauda longa, preservando ordem. */
export function partitionTemplateRowsByShare(
  rows: TemplateSendAggregateRow[],
  totalSends: number,
  thresholdPercent = TEMPLATE_SEND_PROMINENT_SHARE_THRESHOLD,
): PartitionedTemplateRows {
  return partitionRowsByShare(rows, totalSends, thresholdPercent);
}

/** Percentual de participação de um modelo no total de envios. */
export function templateSendSharePercent(
  sendCount: number,
  totalSends: number,
): number {
  return roundSharePercent(sendCount, totalSends);
}
